import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { useAuth } from '../App';
import { API, getImageUrl } from '../utils';
import { toast } from 'sonner';
import { 
  Camera, ArrowLeft, Star, MapPin, DollarSign, 
  CheckCircle, MessageCircle, User, HelpCircle, Paperclip, ExternalLink
} from 'lucide-react';

const ProjectApplications = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [project, setProject] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [hiring, setHiring] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const config = { withCredentials: true, headers };

  useEffect(() => {
    fetchProject();
    fetchApplications();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Project not found');
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/applications`, config);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHire = async (applicationId) => {
    setHiring(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/hire/${applicationId}`, {}, config);
      toast.success('Photographer hired successfully! Redirecting to agreement...');
      
      // Redirect to the auto-created booking's agreement page
      if (response.data.booking_id) {
        setTimeout(() => {
          navigate(`/bookings/${response.data.booking_id}/agreement`);
        }, 1000);
      } else {
        fetchProject();
        fetchApplications();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hire');
      setHiring(false);
    }
  };

  const handlePass = async (applicationId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/pass/${applicationId}`, {}, config);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to pass applicant');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Header */}
      <div className="bg-white border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/projects" className="text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">{project?.title}</h1>
                <p className="text-muted-foreground text-sm">{applications.length} applications</p>
              </div>
            </div>
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Camera className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {project?.status === 'closed' && (
          <div className="mb-6 p-4 rounded-xl bg-blue-50 text-blue-700">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span>A creative has been hired for this project</span>
            </div>
          </div>
        )}

        {applications.length > 0 ? (
          <div className="space-y-4">
            {applications.map((app) => (
              <div
                key={app.application_id}
                className="bg-white rounded-2xl p-6"
                data-testid={`application-${app.application_id}`}
              >
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Creative Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-secondary overflow-hidden flex-shrink-0">
                      {app.creative_picture ? (
                        <img src={getImageUrl(app.creative_picture)} alt={app.creative_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{app.creative_name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                          {app.status === 'rejected' ? 'Reviewed' : app.status}
                        </span>
                      </div>
                      {app.creative_profile && (
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{app.creative_profile.title}</span>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            {app.creative_profile.rating?.toFixed(1) || 'New'}
                          </div>
                          {app.creative_profile.city && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {app.creative_profile.city}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Proposed Rate */}
                  <div className="md:ml-auto text-right">
                    <p className="text-2xl font-bold text-primary">${app.proposed_rate}</p>
                    <p className="text-sm text-muted-foreground">Proposed rate</p>
                  </div>
                </div>

                {/* Cover Letter */}
                <div className="mt-4 p-4 rounded-xl bg-secondary/30">
                  <p className="text-sm font-medium mb-2">Cover Letter</p>
                  <p className="text-muted-foreground">{app.cover_letter}</p>
                </div>
                
                {/* Questions from Creative - NEW */}
                {app.questions && (
                  <div className="mt-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <HelpCircle className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-medium text-blue-800">Questions from Photographer</p>
                    </div>
                    <p className="text-blue-700">{app.questions}</p>
                  </div>
                )}
                
                {/* Attachment - NEW */}
                {app.attachment_url && (
                  <div className="mt-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">Attached Sample</span>
                      </div>
                      <a 
                        href={app.attachment_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        View Attachment
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
                
                {/* Revisions info */}
                <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Includes {app.proposed_revisions === 0 ? 'unlimited' : app.proposed_revisions} revision{app.proposed_revisions !== 1 ? 's' : ''}</span>
                </div>

                {/* Badges */}
                {app.creative_profile?.badges?.length > 0 && (
                  <div className="mt-4 flex gap-2">
                    {app.creative_profile.badges.map((badge, idx) => (
                      <span key={idx} className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium">
                        {badge.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 flex gap-3">
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() => window.open(`/photographers/${app.creative_name?.replace(' ', '')}_${app.creative_profile?.profile_id?.replace('profile_', '')}`, '_blank')}
                  >
                    View Full Profile
                  </Button>
                  <Link to={`/messages`} state={{ partnerId: app.creative_id }}>
                    <Button variant="outline" className="rounded-full">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                  </Link>
                  {project?.status === 'open' && app.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => handleHire(app.application_id)}
                        className="rounded-full bg-green-600 hover:bg-green-700"
                        disabled={hiring}
                        data-testid="hire-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {hiring ? 'Hiring...' : 'Hire'}
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-full text-red-500 border-red-200 hover:bg-red-50"
                        onClick={() => handlePass(app.application_id)}
                      >
                        Pass
                      </Button>
                    </>
                  )}
                  {app.status === 'accepted' && (
                    <span className="flex items-center gap-2 text-green-600 font-medium">
                      <CheckCircle className="w-5 h-5" />
                      Hired
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl">
            <User className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-2xl font-semibold mb-2">No applications yet</h3>
            <p className="text-muted-foreground">Creatives will start applying soon</p>
          </div>
        )}
      </div>

      {/* Profile Dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Photographer Profile</DialogTitle>
          </DialogHeader>
          {selectedApp?.creative_profile && (
            <div className="space-y-6 mt-4">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-xl bg-secondary overflow-hidden">
                  {selectedApp.creative_picture ? (
                    <img src={getImageUrl(selectedApp.creative_picture)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-10 h-10 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-xl">{selectedApp.creative_name}</h3>
                  <p className="text-muted-foreground">{selectedApp.creative_profile.title}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {selectedApp.creative_profile.rating?.toFixed(1) || 'New'} ({selectedApp.creative_profile.total_reviews} reviews)
                    </div>
                    <span>${selectedApp.creative_profile.hourly_rate}/hr</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">About</h4>
                <p className="text-muted-foreground">{selectedApp.creative_profile.bio || 'No bio provided'}</p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Specialties</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedApp.creative_profile.specialties?.map((s, idx) => (
                    <span key={idx} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              {selectedApp.creative_profile.portfolio_items?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Portfolio</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedApp.creative_profile.portfolio_items.slice(0, 6).map((item, idx) => (
                      <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-secondary">
                        <img src={item.url} alt={item.title} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Link to={`/creatives/${selectedApp.creative_profile.profile_id}`}>
                <Button className="w-full rounded-full">View Full Profile</Button>
              </Link>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectApplications;
